/* eslint-disable */
import {
	createSlice,
	type PayloadAction,
} from "@reduxjs/toolkit";
import { isEqual } from "lodash";

import {
	DatasetStepEnum,
	type DatasetCreationTypeEnum,
	type TLedgerSanitizationState,
} from "../../types/ledger.type";
import {
	allDatasetStepMapping,
	extractQuestionsDataset,
	getDatasetSanitizationSteps,
} from "../../utils/ledgerUtils";

import {
	fetchDatasetSpecsQuestionsThunk,
	fetchDatasetStatsThunk,
	getDatasetMetaThunk,
	getGeneralMetaDataThunk,
	getPerfDataFromDataRange,
	getPerfDataRangeThunk,
	getStaticDataFromDataRange,
	getStaticDataRangeThunk,
	uploadFileThunk,
} from "./ledgerSanitization.thunk";

const resetStates = (keys: Array<string>, state: any) => {
	keys.forEach((key) => {
		if (state[key].data !== null) {
			state[key] =
				initialState[key as keyof TLedgerSanitizationState];
		}
	});
};

const initialState: TLedgerSanitizationState = {
	activeDatasetMeta: null,
	allSteps: Object.values(DatasetStepEnum),
	method: null,
	activeStep: DatasetStepEnum.STEP_DATASET_SPECS,
	nextStep: null,
	staticDatasetUploadStatus: {
		status: "not-started",
		error: null,
		loading: false,
	},
	perfDatasetUploadStatus: {
		status: "not-started",
		error: null,
		loading: false,
	},
	generalMeta: null,
	tapesLoad: null,
	datasetSanitizationMeta: null,
	datasetSanitizationMetaStatus: {
		loading: false,
		error: null,
	},

	staticDataRangeSelection: null,
	staticDataRangeStatus: {
		loading: false,
		error: null,
		shortPreviewLoading: false,
	},
	perfDataRangeSelection: null,
	perfDataRangeStatus: {
		loading: false,
		error: null,
		shortPreviewLoading: false,
	},
	specsQuestions: {
		data: null,
		loading: false,
		error: null,
	},
	reviewStats: {
		data: null,
		loading: false,
		error: null,
	},

	error: null,
	submissionStatus: null,
};

const ledgerSanitizationSlice = createSlice({
	name: "ledgerSanitization",
	initialState,
	reducers: {
		resetDatasetUpload: (
			state,
			action: PayloadAction<boolean>
		) => {
			const statusKey = action.payload
				? "staticDatasetUploadStatus"
				: "perfDatasetUploadStatus";
			state[statusKey] = {
				status: "not-started",
				loading: false,
				error: null,
			};
			if (action.payload) {
				state.staticDataRangeSelection = null;
			} else {
				state.perfDataRangeSelection = null;
			}
		},
		resetEverything: () => {
			return { ...initialState };
		},
		updateActiveDatasetMeta: (state, action) => {
			state.activeDatasetMeta = action.payload;
		},
		updateRangeSelection: (state, action) => {
			const { sheet, headerRange, dataRange, isStatic } =
				action.payload;
			if (isStatic) {
				if (state.staticDataRangeSelection) {
					state.staticDataRangeSelection.recentRangeSelection =
						{
							sheet,
							headerRange,
							dataRange,
						};
				}
			} else {
				if (state.perfDataRangeSelection) {
					state.perfDataRangeSelection.recentRangeSelection =
						{
							sheet,
							headerRange,
							dataRange,
						};
				}
			}
		},
		updateCurrentStep: (state, action) => {
			state.tapesLoad = action.payload.tapes_load ?? null;
			if (state.datasetSanitizationMeta) {
				const currentStepIndex =
					action.payload.all_steps.findIndex(
						(el: string) =>
							el === action.payload.current_step
					);

				const datasetStepMapping =
					getDatasetSanitizationSteps(
						state.method as DatasetCreationTypeEnum
					);

				state.datasetSanitizationMeta = {
					allSteps:
						action.payload.all_steps.map(
							(el: string, index: number) => {
								const step =
									datasetStepMapping[el as DatasetStepEnum];
								return {
									...step,
									stepId: el as DatasetStepEnum,
									nextStep:
										action.payload.all_steps[index + 1] ??
										null,
									isCompleted: currentStepIndex > index,
								};
							}
						) ?? [],
					currentStep: {
						...datasetStepMapping[
							action.payload.current_step as DatasetStepEnum
						],
						stepId: action.payload
							.current_step as DatasetStepEnum,
						nextStep:
							action.payload.all_steps[
								currentStepIndex + 1
							] ?? null,
						isCompleted: false,
					},
					datasetName: action.payload.dataset_name,
					quickStart: action.payload.quick_start ?? {},
					isApproved:
						action.payload.current_step ===
						DatasetStepEnum.STEP_DATASET_COMPLETE,
				};
				state.submissionStatus = {
					currentStep:
						state.datasetSanitizationMeta.currentStep
							.stepId,
					nextStep: state.datasetSanitizationMeta
						.currentStep.nextStep as DatasetStepEnum,
					submissionCreated: false,
					isSubmissionSuccessful: false,
					error: null,
				};
			}
		},
		updateSanitizationSubmissionStatus: (state, action) => {
			state.submissionStatus = action.payload;
		},
		markReviewStatsInProgress: (state) => {
			state.reviewStats.data = {
				status: "IN_PROGRESS",
			};
			state.reviewStats.loading = false;
			state.reviewStats.error = null;
		},
		updateActiveStep: (state, action) => {
			state.activeStep = action.payload;
		},
		updateCurrentSchemaId: (state, action) => {
			if (state.activeDatasetMeta) {
				state.activeDatasetMeta.currentSchemaId =
					action.payload;
			}
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(uploadFileThunk.pending, (state) => {
				if (
					state.activeStep ===
					DatasetStepEnum.STEP_STATIC_UPLOAD
				) {
					state.staticDatasetUploadStatus.status =
						"in-progress";
					state.staticDatasetUploadStatus.loading = true;
					state.staticDatasetUploadStatus.error = null;
				} else if (
					state.activeStep ===
					DatasetStepEnum.STEP_PERF_UPLOAD
				) {
					state.perfDatasetUploadStatus.status =
						"in-progress";
					state.perfDatasetUploadStatus.loading = true;
					state.perfDatasetUploadStatus.error = null;
				}
			})
			.addCase(
				uploadFileThunk.fulfilled,
				(state, action) => {
					const payload = action.payload;

					const allSheets = Object.keys(
						payload.sheet_names
					);
					const previewRange = payload.preview_data_range;
					const selectedSheet = previewRange.sheet_name;

					state.nextStep = payload.next_step;

					if (
						state.activeStep ===
						DatasetStepEnum.STEP_STATIC_UPLOAD
					) {
						state.staticDatasetUploadStatus.status =
							"completed";
						state.staticDatasetUploadStatus.loading = false;
						state.staticDatasetUploadStatus.error = null;
						state.error = null;

						state.staticDataRangeSelection = {
							sheets: allSheets,
							selectedSheet,
							allSheetsData: Object.entries(
								payload.sheet_names
							).map(([key, value]: any) => {
								return {
									sheetName: key,
									meta: value,
								};
							}),
							suggestedRange: null,
						};
					} else if (
						state.activeStep ===
						DatasetStepEnum.STEP_PERF_UPLOAD
					) {
						state.perfDatasetUploadStatus.status =
							"completed";
						state.perfDatasetUploadStatus.loading = false;
						state.perfDatasetUploadStatus.error = null;
						state.error = null;

						state.perfDataRangeSelection = {
							sheets: allSheets,
							selectedSheet,
							allSheetsData: Object.entries(
								payload.sheet_names
							).map(([key, value]: any) => {
								return {
									sheetName: key,
									meta: value,
								};
							}),
							suggestedRange: null,
						};
					}
				}
			)
			.addCase(
				uploadFileThunk.rejected,
				(state, action) => {
					if (
						state.activeStep ===
						DatasetStepEnum.STEP_STATIC_UPLOAD
					) {
						state.staticDatasetUploadStatus.loading = false;
						state.staticDatasetUploadStatus.status =
							"failed";
						state.staticDatasetUploadStatus.error =
							action.payload as any;
					} else if (
						state.activeStep ===
						DatasetStepEnum.STEP_PERF_UPLOAD
					) {
						state.perfDatasetUploadStatus.loading = false;
						state.perfDatasetUploadStatus.status = "failed";
						state.perfDatasetUploadStatus.error =
							action.payload as any;
					}
				}
			);

		builder
			.addCase(getGeneralMetaDataThunk.pending, (state) => {
				state.error = null;
			})
			.addCase(
				getGeneralMetaDataThunk.fulfilled,
				(state, action) => {
					state.generalMeta = action.payload;
				}
			)
			.addCase(
				getGeneralMetaDataThunk.rejected,
				(state, action) => {
					state.error = action.payload as string;
				}
			);

		builder
			.addCase(getDatasetMetaThunk.pending, (state) => {
				state.datasetSanitizationMetaStatus.loading = true;
				state.datasetSanitizationMetaStatus.error = null;
			})
			.addCase(
				getDatasetMetaThunk.fulfilled,
				(state, action) => {
					state.tapesLoad =
						action.payload.tapes_load ?? null;
					if (
						action.payload.all_steps &&
						action.payload.current_step
					) {
						state.activeDatasetMeta = {
							datasetId: action.payload.dataset_id,
							datasetName: action.payload.dataset_name,
							signatures: Array.isArray(
								action.payload.signatures
							)
								? {}
								: (action.payload.signatures ?? {}),
							currentSchemaId:
								action.payload.current_schema_id ?? "",
						};

						const method = action.payload.dataset_type;

						state.method =
							method as DatasetCreationTypeEnum;

						const datasetStepMapping =
							getDatasetSanitizationSteps(
								method as DatasetCreationTypeEnum
							);

						const currentStepIndex =
							action.payload.all_steps.findIndex(
								(el: string) =>
									el === action.payload.current_step
							);

						state.datasetSanitizationMeta = {
							allSteps:
								action.payload.all_steps.map(
									(el: string, index: number) => {
										const step =
											datasetStepMapping[
												el as DatasetStepEnum
											];
										return {
											...step,
											stepId: el as DatasetStepEnum,
											nextStep:
												action.payload.all_steps[
													index + 1
												] ?? null,
											isCompleted: currentStepIndex > index,
										};
									}
								) ?? [],
							currentStep: {
								...datasetStepMapping[
									action.payload
										.current_step as DatasetStepEnum
								],
								stepId: action.payload
									.current_step as DatasetStepEnum,
								nextStep:
									action.payload.all_steps[
										currentStepIndex + 1
									] ?? null,
								isCompleted: false,
							},
							datasetName: action.payload.dataset_name,
							quickStart: action.payload.quick_start ?? {},
							isApproved:
								action.payload.current_step ===
								DatasetStepEnum.STEP_DATASET_COMPLETE,
						};
					} else {
						state.datasetSanitizationMeta = {
							allSteps: [],
							currentStep: {
								...allDatasetStepMapping[
									DatasetStepEnum.STEP_DATASET_COMPLETE
								],
								stepId:
									DatasetStepEnum.STEP_DATASET_COMPLETE,
								isCompleted: true,
							},
							datasetName: "",
							isApproved: false,
						};
					}
					state.datasetSanitizationMetaStatus.loading =
						false;
					state.datasetSanitizationMetaStatus.error = null;
				}
			)
			.addCase(
				getDatasetMetaThunk.rejected,
				(state, action) => {
					state.datasetSanitizationMetaStatus.loading =
						false;
					state.datasetSanitizationMetaStatus.error =
						action.payload ?? action.error;
					state.datasetSanitizationMeta = null;
					state.activeDatasetMeta = null;
				}
			);

		builder
			.addCase(getStaticDataRangeThunk.pending, (state) => {
				state.error = null;
				state.staticDataRangeStatus.loading = true;
				state.staticDataRangeStatus.error = null;
				state.staticDataRangeStatus.statusCode = undefined;
				state.staticDataRangeStatus.shortPreviewLoading =
					true;
			})
			.addCase(
				getStaticDataRangeThunk.fulfilled,
				(state, action) => {
					state.error = null;

					const payload = action.payload;

					const preview = action.payload.preview_data_range;

					const suggestion = {
						sheetName: preview.sheet_name,
						headerRange: preview.header_range,
						bodyRange: preview.body_range,
					};

					const allSheets = Object.keys(
						payload.sheet_bounds
					);
					const selectedSheet = suggestion.sheetName;

					const confirmData = payload.confirmed_data_range;
					const isPreviouslyConfirmed =
						Boolean(confirmData);
					const isModifiedAndConfirmed = isEqual(
						confirmData,
						preview
					);

					state.staticDataRangeSelection = {
						sheets: allSheets,
						selectedSheet,
						allSheetsData: Object.entries(
							payload.sheet_bounds
						).map(([key, value]: any) => {
							return {
								sheetName: key,
								meta: value,
							};
						}),
						suggestedRange: suggestion,
						recentRangeSelection: {
							sheet: suggestion.sheetName,
							headerRange: suggestion.headerRange,
							dataRange: suggestion.bodyRange,
						},
						isStepConfirmed: isPreviouslyConfirmed,
						isModifiedNotConfirmed: !isModifiedAndConfirmed,
					};
					state.staticDataRangeStatus.loading = false;
					state.staticDataRangeStatus.error = null;
					state.staticDataRangeStatus.statusCode =
						undefined;
					state.staticDataRangeStatus.shortPreviewLoading =
						false;
				}
			)
			.addCase(
				getStaticDataRangeThunk.rejected,
				(state, action) => {
					state.error = action.payload as string;
					state.staticDataRangeSelection = null;
					state.staticDataRangeStatus.loading = false;
					// payload is the AlloanException body ({error_code, msg, ...}); store only the
					// message string, never the object (it gets rendered as a React child).
					state.staticDataRangeStatus.error =
						(action.payload as any)?.msg ??
						"Failed to load data range";
					state.staticDataRangeStatus.statusCode = (
						action.payload as any
					)?.status;
					state.staticDataRangeStatus.shortPreviewLoading =
						false;
				}
			);

		builder
			.addCase(getPerfDataRangeThunk.pending, (state) => {
				state.error = null;
				state.perfDataRangeStatus.loading = true;
				state.perfDataRangeStatus.error = null;
				state.perfDataRangeStatus.statusCode = undefined;
				state.perfDataRangeStatus.shortPreviewLoading =
					true;
			})
			.addCase(
				getPerfDataRangeThunk.fulfilled,
				(state, action) => {
					state.error = null;

					const payload = action.payload;

					const preview = action.payload.preview_data_range;

					const suggestion = {
						sheetName: preview.sheet_name,
						headerRange: preview.header_range,
						bodyRange: preview.body_range,
					};

					const allSheets = Object.keys(
						payload.sheet_bounds
					);
					const selectedSheet = suggestion.sheetName;

					const confirmData = payload.confirmed_data_range;
					const isPreviouslyConfirmed =
						Boolean(confirmData);
					const isModifiedAndConfirmed = isEqual(
						confirmData,
						preview
					);

					state.perfDataRangeSelection = {
						sheets: allSheets,
						selectedSheet,
						allSheetsData: Object.entries(
							payload.sheet_bounds
						).map(([key, value]: any) => {
							return {
								sheetName: key,
								meta: value,
							};
						}),
						suggestedRange: suggestion,
						recentRangeSelection: {
							sheet: suggestion.sheetName,
							headerRange: suggestion.headerRange,
							dataRange: suggestion.bodyRange,
						},
						isStepConfirmed: isPreviouslyConfirmed,
						isModifiedNotConfirmed: !isModifiedAndConfirmed,
					};
					state.perfDataRangeStatus.loading = false;
					state.perfDataRangeStatus.error = null;
					state.perfDataRangeStatus.statusCode = undefined;
					state.perfDataRangeStatus.shortPreviewLoading =
						false;
				}
			)
			.addCase(
				getPerfDataRangeThunk.rejected,
				(state, action) => {
					state.error = action.payload as string;
					state.perfDataRangeSelection = null;
					state.perfDataRangeStatus.loading = false;
					// payload is the AlloanException body ({error_code, msg, ...}); store only the
					// message string, never the object (it gets rendered as a React child).
					state.perfDataRangeStatus.error =
						(action.payload as any)?.msg ??
						"Failed to load data range";
					state.perfDataRangeStatus.statusCode = (
						action.payload as any
					)?.status;
					state.perfDataRangeStatus.shortPreviewLoading =
						false;
				}
			);

		builder
			.addCase(
				getStaticDataFromDataRange.pending,
				(state) => {
					state.staticDataRangeStatus.loading = true;
					state.error = null;
					state.staticDataRangeStatus.shortPreviewLoading =
						true;
					state.staticDataRangeStatus.error = null;
					state.staticDataRangeStatus.statusCode =
						undefined;
				}
			)
			.addCase(
				getStaticDataFromDataRange.fulfilled,
				(state, action) => {
					const payload = action.payload;
					state.staticDataRangeStatus.loading = false;
					state.error = null;
					if (
						state.staticDataRangeSelection &&
						(Array.isArray(payload) || payload.table)
					) {
						state.staticDataRangeSelection.shortPreview =
							Array.isArray(payload)
								? payload
								: payload.table;
					}
					state.staticDataRangeStatus.shortPreviewLoading =
						false;
					state.staticDataRangeStatus.error = null;
					state.staticDataRangeStatus.statusCode =
						undefined;
				}
			)
			.addCase(
				getStaticDataFromDataRange.rejected,
				(state, action) => {
					state.staticDataRangeStatus.loading = false;
					state.error = action.payload as string;
					state.staticDataRangeStatus.shortPreviewLoading =
						false;
					state.staticDataRangeStatus.error = (
						action.payload as any
					)?.msg as string;

					state.staticDataRangeStatus.statusCode = (
						action.payload as any
					).status;
					if (state.staticDataRangeSelection) {
						state.staticDataRangeSelection.shortPreview =
							undefined;
					}
				}
			);

		builder
			.addCase(
				getPerfDataFromDataRange.pending,
				(state) => {
					state.perfDataRangeStatus.loading = true;
					state.error = null;
					state.perfDataRangeStatus.shortPreviewLoading =
						true;
					state.perfDataRangeStatus.error = null;
					state.perfDataRangeStatus.statusCode = undefined;
				}
			)
			.addCase(
				getPerfDataFromDataRange.fulfilled,
				(state, action) => {
					const payload = action.payload;
					state.perfDataRangeStatus.loading = false;
					state.error = null;
					if (
						state.perfDataRangeSelection &&
						payload.table
					) {
						state.perfDataRangeSelection.shortPreview =
							payload.table;
					}
					state.perfDataRangeStatus.shortPreviewLoading =
						false;
					state.perfDataRangeStatus.error = null;
					state.perfDataRangeStatus.statusCode = undefined;
				}
			)
			.addCase(
				getPerfDataFromDataRange.rejected,
				(state, action) => {
					state.perfDataRangeStatus.loading = false;
					state.error = action.payload as string;
					state.perfDataRangeStatus.shortPreviewLoading =
						false;
					state.perfDataRangeStatus.error = (
						action.payload as any
					)?.msg as string;

					state.perfDataRangeStatus.statusCode = (
						action.payload as any
					).status;
					if (state.perfDataRangeSelection) {
						state.perfDataRangeSelection.shortPreview =
							undefined;
					}
				}
			);

		builder
			.addCase(
				fetchDatasetSpecsQuestionsThunk.pending,
				(state) => {
					state.specsQuestions.loading = true;
					state.specsQuestions.error = null;
					const keys = ["specsQuestions"];
					resetStates(keys, state);
				}
			)
			.addCase(
				fetchDatasetSpecsQuestionsThunk.fulfilled,
				(state, action) => {
					const questions = extractQuestionsDataset(
						action.payload
					);
					state.specsQuestions.data = questions;

					state.specsQuestions.loading = false;
					state.specsQuestions.error = null;
				}
			)
			.addCase(
				fetchDatasetSpecsQuestionsThunk.rejected,
				(state, action) => {
					state.specsQuestions.error = action.payload;
					state.specsQuestions.loading = false;
				}
			);
		builder
			.addCase(fetchDatasetStatsThunk.pending, (state) => {
				state.reviewStats.loading = true;
				state.reviewStats.error = null;
			})
			.addCase(
				fetchDatasetStatsThunk.fulfilled,
				(state, action) => {
					state.reviewStats.data = action.payload;
					state.reviewStats.loading = false;
					state.reviewStats.error = null;
				}
			)
			.addCase(
				fetchDatasetStatsThunk.rejected,
				(state, action) => {
					state.reviewStats.data = null;
					state.reviewStats.loading = false;
					state.reviewStats.error = action.payload;
				}
			);
	},
});

export const LedgerActions =
	ledgerSanitizationSlice.actions;
export const LedgerReducer =
	ledgerSanitizationSlice.reducer;
